/**
 * The brief under Home's greeting: only what needs a hand now, in a
 * sentence or two, each fact a link (homeBrief.logic).
 *
 * @module components/home/HomeBrief
 */
import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { BriefSentence } from "./homeBrief.logic";

const PARAGRAPH = "text-[15px] leading-[1.6] text-pretty text-muted-foreground";
/** Quiet until hovered, as a link in a sentence should be. */
const LINK = "text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground";
const RING_LINK = cn(LINK, "rounded-none outline-none hover:bg-accent/70 focus-visible:ring-2 focus-visible:ring-ring");

function BriefLink({ sentence, orgSegment, children }: { readonly sentence: BriefSentence; readonly orgSegment: string; readonly children: ReactNode }) {
  const className = sentence.ringFocus ? RING_LINK : LINK;
  const { target } = sentence;
  switch (target.kind) {
    case "finding":
      return (
        <Link to="/$org/compliance/findings/$findingId" params={{ org: orgSegment, findingId: target.findingId }} className={className}>
          {children}
        </Link>
      );
    case "findings":
      return (
        <Link to="/$org/compliance/findings" params={{ org: orgSegment }} className={className}>
          {children}
        </Link>
      );
    case "workflow":
      return (
        <Link to="/$org/workflows/$workflowId" params={{ org: orgSegment, workflowId: target.workflowId }} className={className}>
          {children}
        </Link>
      );
    case "workflows":
      return (
        <Link to="/$org/workflows" params={{ org: orgSegment }} className={className}>
          {children}
        </Link>
      );
    case "policy":
      return (
        <Link to="/$org/compliance/policies/$policyId" params={{ org: orgSegment, policyId: target.policyId }} className={className}>
          {children}
        </Link>
      );
    case "regulatory":
      return (
        <Link to="/$org/compliance/regulatory" params={{ org: orgSegment }} className={className}>
          {children}
        </Link>
      );
  }
}

export function HomeBrief({ sentences, orgSegment }: { readonly sentences: ReadonlyArray<BriefSentence>; readonly orgSegment: string }) {
  if (sentences.length === 0) {
    return (
      <p className={PARAGRAPH} data-home-brief="">
        Nothing needs you right now.
      </p>
    );
  }
  return (
    <p className={PARAGRAPH} data-home-brief="">
      {sentences.map((sentence, index) => (
        <Fragment key={sentence.key}>
          {index > 0 ? " " : null}
          <span>
            <BriefLink sentence={sentence} orgSegment={orgSegment}>
              {sentence.link}
            </BriefLink>
            {sentence.rest}
          </span>
        </Fragment>
      ))}
    </p>
  );
}
